import type { UserProfileEComm } from './user-profile.ts'
import { ECommItem, ECommItemWithRating, ECommItemWithoutQuantity, ECommItemWithQuantity } from '../types/ecomm.ts'

/**
 * @public
 * @deprecated Use `PushlySDK.UserProfile` catalog methods instead.
 */
export type EComm = {
    /**
     * Sends "save_item" event with passed {@link ECommItemWithoutQuantity} data.
     *
     * Fire this interaction when a user views an item on your site.
     *
     * @example
     * ```typescript
     * PushlySDK.EComm.trackSavedItem({
     *     id: 'PRODUCT_ID',
     * })
     * ```
     */
    trackSavedItem(item: ECommItemWithoutQuantity): Promise<void>
    /**
     * Sends "save_item" event with passed {@link ECommItemWithoutQuantity} data.
     *
     * Fire this interaction when a user views an item on your site.
     *
     * @example
     * ```typescript
     * PushlySDK.EComm.trackSavedItems([
     *   {
     *     id: 'PRODUCT_ID',
     *   }
     * ])
     * ```
     */
    trackSavedItems(items: ECommItemWithoutQuantity[]): Promise<void>
    /**
     * Sends "complete_item" event with passed {@link ECommItemWithoutQuantity} data.
     *
     * Fire this interaction when a user completes an item.
     */
    trackCompletedItem(item: ECommItemWithoutQuantity): Promise<void>
    /**
     * Sends "complete_item" event with passed {@link ECommItemWithoutQuantity} data.
     *
     * Fire this interaction when a user completes items.
     */
    trackCompletedItems(items: ECommItemWithoutQuantity[]): Promise<void>
    /**
     * Sends "unsave_item" event with passed {@link ECommItemWithoutQuantity} data.
     */
    unsaveItem(item: ECommItemWithoutQuantity): Promise<void>
    /**
     * Sends "unsave_item" event with passed {@link ECommItemWithoutQuantity} data.
     */
    unsaveItems(items: ECommItemWithoutQuantity[]): Promise<void>
    /**
     * Sends "uncomplete_item" event with passed {@link ECommItemWithoutQuantity} data.
     */
    uncompleteItem(item: ECommItemWithoutQuantity): Promise<void>
    /**
     * Sends "uncomplete_item" event with passed {@link ECommItemWithoutQuantity} data.
     */
    uncompleteItems(items: ECommItemWithoutQuantity[]): Promise<void>
    /**
     * Sends "unrate_item" event with passed {@link ECommItemWithoutQuantity} data.
     */
    unrateItem(item: ECommItemWithoutQuantity): Promise<void>
    /**
     * Sends "unrate_item" event with passed {@link ECommItemWithoutQuantity} data.
     */
    unrateItems(items: ECommItemWithoutQuantity[]): Promise<void>
    /**
     * Sends "rate_item" event with passed {@link ECommItemWithRating} data.
     *
     * Fire this interaction when a user rates an item.
     */
    trackRatedItem(item: ECommItemWithRating): Promise<void>
    /**
     * Sends "rate_item" event with passed {@link ECommItemWithRating} data.
     *
     * Fire this interaction when a user rates items.
     */
    trackRatedItems(items: ECommItemWithRating[]): Promise<void>
    /**
     * Sends "view_item" event with passed {@link ECommItemWithoutQuantity} data.
     *
     * Fire this interaction when a user views an item on your site.
     *
     * @example
     * ```typescript
     * PushlySDK.EComm.trackViewedItem({
     *     id: 'PRODUCT_ID',
     * })
     * ```
     */
    trackViewedItem(item: ECommItemWithoutQuantity): Promise<void>
    /**
     * Sends "view_item" event with passed {@link ECommItemWithoutQuantity} data.
     *
     * Fire this interaction when a user views an item on your site.
     *
     * @example
     * ```typescript
     * PushlySDK.EComm.trackViewedItems([
     *   {
     *     id: 'PRODUCT_ID',
     *   }
     * ])
     * ```
     */
    trackViewedItems(items: ECommItemWithoutQuantity[]): Promise<void>

    /**
     * Sends "add_to_cart" event with passed {@link ECommItem} data.
     *
     * Fire this interaction whenever a user adds an item to their shopping cart.
     *
     * **addToCart** May be called as many times as necessary to keep track of all items in a visitor's cart. Abandoned
     * Cart notifications may be sent for any item in a customer's cart that has not been purchased.
     *
     * After a purchase is made the visitor's cart will be emptied and no notifications for a purchased item will be sent to the visitor.
     *
     * @example
     * ```typescript
     * PushlySDK.EComm.addToCart([
     *   {
     *     id: 'PRODUCT_ID',
     *     quantity: 1
     *   }
     * ])
     * ```
     */
    addToCart(items: ECommItemWithQuantity[]): Promise<void>

    /**
     * Sends "update_cart" event with passed {@link ECommItem} data.
     *
     * Fire this interaction after a visitor makes a change to their cart.
     *
     * This interaction can be used to change the properties on a cart item or remove an item from the visitor's cart.
     *
     * Ex: To remove an item from the cart call the update_cart method with the full current cart information (omitting the removed item):
     *
     * @example
     * ```typescript
     * PushlySDK.EComm.updateCart([
     *   {
     *     id: 'PRODUCT_ID',
     *     quantity: 1
     *   }
     * ])
     * ```
     */
    updateCart(items: ECommItemWithQuantity[]): Promise<void>

    /**
     * Sends "update_cart" event that empties all data currently stored in this subscriber's cart.
     *
     * Fire this interaction whenever a user clears their shopping cart.
     *
     * @example
     * ```typescript
     * PushlySDK.EComm.clearCart()
     * ```
     */
    clearCart(): Promise<void>

    /**
     * Sends "purchase" event with either items currently in the cart or with passed {@link ECommItem} data.
     *
     * Fire this interaction after a visitor successfully completes their cart purchase.
     *
     * Providing this interaction will ensure that visitors do not receive notifications for items they have previously
     * purchased. This interaction will also enable revenue attribution.
     *
     * @example
     * ```typescript
     * PushlySDK.EComm.trackPurchase()
     * ```
     */
    trackPurchase(): Promise<void>
    /**
     * Sends "purchase" event with either items currently in the cart or with passed {@link ECommItem} data.
     *
     * Fire this interaction after a visitor successfully completes their cart purchase.
     *
     * Providing this interaction will ensure that visitors do not receive notifications for items they have previously
     * purchased. This interaction will also enable revenue attribution.
     *
     * @example
     * ```typescript
     * PushlySDK.EComm.trackPurchase(
     *   [{
     *     id: 'PRODUCT_ID',
     *     quantity: 1
     *   }]
     * )
     * ```
     */
    trackPurchase(items: ECommItem[]): Promise<void>
    /**
     * Sends "purchase" event with either items currently in the cart or with passed {@link ECommItem} data.
     *
     * Fire this interaction after a visitor successfully completes their cart purchase.
     *
     * Providing this interaction will ensure that visitors do not receive notifications for items they have previously
     * purchased. This interaction will also enable revenue attribution.
     *
     * While the **purchaseId** and **priceValue** params are not required it is recommended that they are provided.
     *
     * @example
     * ```typescript
     * PushlySDK.EComm.trackPurchase(
     *   [{
     *     id: 'PRODUCT_ID',
     *     quantity: 1
     *   }],
     *   '344.33',
     *   'ABC123'
     * )
     * ```
     */
    trackPurchase(items?: ECommItem[], purchaseId?: string, priceValue?: string): Promise<void>
}

/** @internal */
export class ECommImpl implements EComm {
    readonly #userProfile: UserProfileEComm

    public constructor(userProfile: UserProfileEComm) {
        this.#userProfile = userProfile
    }

    public async trackSavedItem(item: ECommItemWithoutQuantity): Promise<void> {
        return this.#userProfile.saveItem(item)
    }

    public async trackSavedItems(items: ECommItemWithoutQuantity[]): Promise<void> {
        return this.#userProfile.saveItems(items)
    }

    public async trackCompletedItem(item: ECommItemWithoutQuantity): Promise<void> {
        return this.#userProfile.completeItem(item)
    }

    public async trackCompletedItems(items: ECommItemWithoutQuantity[]): Promise<void> {
        return this.#userProfile.completeItems(items)
    }

    public async unsaveItem(item: ECommItemWithoutQuantity): Promise<void> {
        return this.#userProfile.unsaveItem(item)
    }

    public async unsaveItems(items: ECommItemWithoutQuantity[]): Promise<void> {
        return this.#userProfile.unsaveItems(items)
    }

    public async uncompleteItem(item: ECommItemWithoutQuantity): Promise<void> {
        return this.#userProfile.uncompleteItem(item)
    }

    public async uncompleteItems(items: ECommItemWithoutQuantity[]): Promise<void> {
        return this.#userProfile.uncompleteItems(items)
    }

    public async unrateItem(item: ECommItemWithoutQuantity): Promise<void> {
        return this.#userProfile.unrateItem(item)
    }

    public async unrateItems(items: ECommItemWithoutQuantity[]): Promise<void> {
        return this.#userProfile.unrateItems(items)
    }

    public async trackRatedItem(item: ECommItemWithRating): Promise<void> {
        return this.#userProfile.rateItem(item)
    }

    public async trackRatedItems(items: ECommItemWithRating[]): Promise<void> {
        return this.#userProfile.rateItems(items)
    }

    public async trackViewedItem(item: ECommItemWithoutQuantity): Promise<void> {
        return this.#userProfile.viewItem(item)
    }

    public async trackViewedItems(items: ECommItemWithoutQuantity[]): Promise<void> {
        return this.#userProfile.viewItems(items)
    }

    public async addToCart(items: ECommItemWithQuantity[]): Promise<void> {
        return this.#userProfile.addToCart(items)
    }

    public async updateCart(items: ECommItemWithQuantity[]): Promise<void> {
        return this.#userProfile.updateCart(items)
    }

    public async clearCart(): Promise<void> {
        return this.#userProfile.clearCart()
    }

    public async trackPurchase(): Promise<void>
    public async trackPurchase(items: ECommItem[], purchaseId?: string, priceValue?: string): Promise<void>
    public async trackPurchase(items?: ECommItem[], purchaseId?: string, priceValue?: string): Promise<void> {
        return this.#userProfile.purchase(items, purchaseId, priceValue)
    }
}
